import React from 'react'
import { observer } from 'mobx-react'
import JobViewModel from '../JobViewModel'
import Button from '../../../../../components/Button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/Card'
import { formatMoney } from 'utils/money-util'
import styles from './PaymentDistributionCard.sass'
import { Dropdown, DropdownTitle, DropdownItem } from 'components/Dropdown'
import { formatToLongDate } from 'utils/date-util'
import Tooltip from 'components/Tooltip'

/**
 * Payment Distribution Item Props.
 */
interface PaymentDistributionItemProps {
  label: string
  value: string
  dataTest?: string
  button?: React.ReactNode
}

/**
 * Payment Distribution Item.
 */
const PaymentDistributionItem = ({
  label,
  value,
  dataTest,
  button,
}: PaymentDistributionItemProps) => {
  // After this threshold, text gets an ellipsis style: ...
  // with a tooltip.
  const maxDisplayLength = 20

  return (
    <div className={styles.paymentDistributionItem}>
      <div className={styles.paymentDistributionItemLabel}>{label}</div>
      <div className={styles.paymentDistributionItemValueContainer}>
        <div
          className={styles.paymentDistributionItemValue}
          data-test={dataTest}
        >
          {value.length <= maxDisplayLength ? (
            <span>{value}</span>
          ) : (
            <Tooltip placement="top" content={value}>
              <span>{value}</span>
            </Tooltip>
          )}
        </div>
        <div className={styles.paymentDistributionItemButton}>{button}</div>
      </div>
    </div>
  )
}

/**
 * Coupon removal button props.
 */
interface CouponRemovalButtonProps {
  vm: JobViewModel
}

/**
 * Coupon removal button.
 */
const CouponRemovalButton = ({ vm }: CouponRemovalButtonProps) => {
  return (
    <Button
      type="text"
      size="baseline"
      dataTest="job-coupon-remove"
      onClick={vm.removeCouponAsAdminVM.show}
    >
      Remove
    </Button>
  )
}

/**
 * Payment Distribution button props.
 */
interface PaymentDistributionButtonProps {
  vm: JobViewModel
}

/**
 * Payment Distribution button.
 */
const PaymentDistributionButton = ({ vm }: PaymentDistributionButtonProps) => {
  return (
    <Button
      type="text"
      size="baseline"
      dataTest="job-pro-cut-amount-edit-split"
      onClick={vm.splitPayoutDialogVM.show}
    >
      Edit split
    </Button>
  )
}

/**
 * Payment Distribution dropdown props.
 */
interface PaymentDistributionDropdownProps {
  vm: JobViewModel
}

/**
 * Payment Distribution dropdown.
 */
const PaymentDistributionDropdown = observer(
  ({ vm }: PaymentDistributionDropdownProps) => {
    return vm.jobPurchaseTransactions.length === 1 ? (
      <Button
        type="text"
        size="baseline"
        dataTest="job-client-paid-amount-view-receipts"
        href={vm.jobPurchaseTransactions.at(0)?.metadata.receipt_url ?? ''}
        target="_blank"
      >
        View receipt
      </Button>
    ) : (
      <Dropdown
        autoDismiss
        position="top-end"
        button={
          <Button
            type="text"
            size="baseline"
            dataTest="job-client-paid-amount-view-receipts"
          >
            View receipts
          </Button>
        }
      >
        <DropdownTitle>View receipts</DropdownTitle>
        {vm.jobPurchaseTransactions.map((t) => (
          <DropdownItem key={t.id}>
            <a
              className={styles.viewReceiptsDropdownItem}
              href={t.metadata.receipt_url ?? ''}
              rel="noopener noreferrer"
              target="_blank"
            >
              {formatMoney(t.amount / 100)} on {formatToLongDate(t.dateCreated)}
            </a>
          </DropdownItem>
        ))}
      </Dropdown>
    )
  },
)

/**
 * Payment Distribution Card Props.
 */
interface PaymentDistributionCardProps {
  vm: JobViewModel
}

/**
 * Payment Distribution Card.
 */
export default observer(function PaymentDistributionCard({
  vm,
}: PaymentDistributionCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment distribution</CardTitle>
      </CardHeader>
      <CardContent scroll>
        <PaymentDistributionItem
          label="Primary amount"
          value={formatMoney(vm.job.primaryAmount)}
          dataTest="job-primary-amount"
        />
        {vm.canViewCoupon && (
          <PaymentDistributionItem
            label="Coupon"
            value={vm.couponDisplay}
            dataTest={`job-applied-coupon-${vm.coupon?.code}`}
            button={
              vm.canRemoveCouponAsAdmin ? <CouponRemovalButton vm={vm} /> : null
            }
          />
        )}
        {vm.job.canViewProCut && (
          <PaymentDistributionItem
            label="Total Pro cut"
            value={formatMoney(vm.job.providerCutAmount)}
            dataTest="job-pro-cut-amount"
            button={
              vm.job.canSplitPayout ? (
                <PaymentDistributionButton vm={vm} />
              ) : null
            }
          />
        )}
        <PaymentDistributionItem
          label="Total"
          value={formatMoney(vm.job.total)}
          dataTest="job-total"
        />
        <PaymentDistributionItem
          label="Client paid"
          value={formatMoney(vm.job.paidAmount)}
          dataTest="job-client-paid-amount"
          button={
            vm.jobPurchaseTransactions.length > 0 &&
            vm.job.paidAmount > 0 && <PaymentDistributionDropdown vm={vm} />
          }
        />
      </CardContent>
    </Card>
  )
})
